import React, { useState, useEffect } from 'react';
import { Bell, Mail, Smartphone, Globe, MapPin, Shield, ChevronRight, Save, CheckCircle, Calendar } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { settingsAPI, vipAPI, authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

export default function SettingsPage() {
  const { user, updateUser } = useAuth();
  const [prefs, setPrefs] = useState({
    email_deals: true, email_orders: true, email_referrals: true, email_marketing: false,
    push_deals: true, push_orders: true, push_referrals: true, push_marketing: false,
  });
  const [vipStatus, setVipStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [locationEnabled, setLocationEnabled] = useState(false);
  const [dob, setDob] = useState('');
  const [savingDob, setSavingDob] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const [prefsRes, vipRes] = await Promise.all([
          settingsAPI.getNotificationPrefs(),
          vipAPI.getStatus().catch(() => ({ data: null })),
        ]);
        setPrefs(prefsRes.data);
        setVipStatus(vipRes.data);
        if (user?.date_of_birth) setDob(user.date_of_birth);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsAPI.updateNotificationPrefs(prefs);
      toast.success('Preferences saved!');
    } catch (e) { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const enableLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          try {
            await settingsAPI.updateLocation(pos.coords.latitude, pos.coords.longitude);
            setLocationEnabled(true);
            toast.success('Location enabled! You\'ll see nearby deals.');
          } catch (e) { toast.error('Failed to save location'); }
        },
        () => toast.error('Location permission denied')
      );
    }
  };

  const handleSaveDob = async () => {
    if (!dob) { toast.error('Please select a date'); return; }
    setSavingDob(true);
    try {
      const res = await authAPI.updateProfile({ date_of_birth: dob });
      updateUser({ date_of_birth: dob });
      toast.success('Date of birth saved!');
    } catch (e) { toast.error('Failed to save'); }
    finally { setSavingDob(false); }
  };

  const toggle = (key) => setPrefs({ ...prefs, [key]: !prefs[key] });

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" /></div>;

  const TIER_COLORS = { bronze: '#CD7F32', silver: '#C0C0C0', gold: '#FFD700', platinum: '#E5E4E2', diamond: '#B9F2FF' };

  return (
    <div className="min-h-screen bg-zinc-50 pb-8">
      <section className="bg-white border-b border-zinc-200">
        <div className="max-w-3xl mx-auto px-4 py-8">
          <h1 className="font-display font-bold text-2xl text-zinc-900" data-testid="settings-title">Settings</h1>
          <p className="text-zinc-500 text-sm">Manage your preferences</p>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* VIP Status */}
        {vipStatus && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-zinc-900 to-zinc-800 rounded-2xl p-6 text-white" data-testid="vip-status-card">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-xs text-zinc-400 uppercase tracking-wider">VIP Status</p>
                <p className="font-display font-bold text-3xl capitalize" style={{ color: TIER_COLORS[vipStatus.current_tier] }}>{vipStatus.current_tier}</p>
              </div>
              <Shield size={40} style={{ color: TIER_COLORS[vipStatus.current_tier] }} />
            </div>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-white/10 rounded-xl p-3">
                <p className="text-xs text-zinc-400">Points</p>
                <p className="font-display font-bold text-xl">{vipStatus.points?.toLocaleString()}</p>
              </div>
              <div className="bg-white/10 rounded-xl p-3">
                <p className="text-xs text-zinc-400">Cashback Rate</p>
                <p className="font-display font-bold text-xl">{(vipStatus.benefits?.cashback_rate * 100)}%</p>
              </div>
            </div>
            {vipStatus.next_tier && (
              <div className="bg-white/5 rounded-xl p-3">
                <p className="text-xs text-zinc-400">Next: <span className="capitalize font-semibold" style={{ color: TIER_COLORS[vipStatus.next_tier.name] }}>{vipStatus.next_tier.name}</span></p>
                <div className="w-full bg-white/10 rounded-full h-2 mt-2">
                  <div className="h-2 rounded-full bg-gradient-to-r from-[#b8ff00] to-[#00ff88]" style={{ width: `${Math.min(100, (vipStatus.points / (vipStatus.points + vipStatus.next_tier.points_needed)) * 100)}%` }} />
                </div>
                <p className="text-xs text-zinc-400 mt-1">{vipStatus.next_tier.points_needed} points to go</p>
              </div>
            )}
            {vipStatus.benefits?.perks && (
              <div className="mt-4 flex flex-wrap gap-2">
                {vipStatus.benefits.perks.map((p, i) => <span key={i} className="text-xs px-2 py-1 bg-white/10 rounded-full">{p}</span>)}
              </div>
            )}
          </motion.div>
        )}

        {/* Email Notifications */}
        <div className="bg-white rounded-2xl border border-zinc-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-blue-100 rounded-lg"><Mail size={18} className="text-blue-600" /></div>
            <h3 className="font-display font-semibold text-lg">Email Notifications</h3>
          </div>
          <div className="space-y-4">
            {[
              { key: 'email_deals', label: 'Flash Deal Alerts', desc: 'Get notified about new flash deals and discounts' },
              { key: 'email_orders', label: 'Order Updates', desc: 'Status changes, shipping, and delivery updates' },
              { key: 'email_referrals', label: 'Referral Rewards', desc: 'When someone uses your referral code' },
              { key: 'email_marketing', label: 'Marketing & Promotions', desc: 'Special offers and new product launches' },
            ].map(({ key, label, desc }) => (
              <div key={key} className="flex items-center justify-between">
                <div><Label className="font-medium">{label}</Label><p className="text-xs text-zinc-500">{desc}</p></div>
                <Switch checked={prefs[key]} onCheckedChange={() => toggle(key)} data-testid={`toggle-${key}`} />
              </div>
            ))}
          </div>
        </div>

        {/* Push Notifications */}
        <div className="bg-white rounded-2xl border border-zinc-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-violet-100 rounded-lg"><Smartphone size={18} className="text-violet-600" /></div>
            <h3 className="font-display font-semibold text-lg">Push Notifications</h3>
          </div>
          <div className="space-y-4">
            {[
              { key: 'push_deals', label: 'Flash Deal Alerts', desc: 'Browser push for live deals' },
              { key: 'push_orders', label: 'Order Updates', desc: 'Real-time order status changes' },
              { key: 'push_referrals', label: 'Referral Activity', desc: 'New referral signups and bonuses' },
              { key: 'push_marketing', label: 'Promotions', desc: 'Special offers and campaigns' },
            ].map(({ key, label, desc }) => (
              <div key={key} className="flex items-center justify-between">
                <div><Label className="font-medium">{label}</Label><p className="text-xs text-zinc-500">{desc}</p></div>
                <Switch checked={prefs[key]} onCheckedChange={() => toggle(key)} data-testid={`toggle-${key}`} />
              </div>
            ))}
          </div>
        </div>

        {/* Date of Birth */}
        <div className="bg-white rounded-2xl border border-zinc-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-rose-100 rounded-lg"><Calendar size={18} className="text-rose-600" /></div>
            <div>
              <h3 className="font-display font-semibold text-lg">Date of Birth</h3>
              <p className="text-xs text-zinc-500">Required for age-restricted content access</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Input
              type="date"
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              className="max-w-xs bg-zinc-50 border-zinc-200 rounded-xl"
              data-testid="dob-input"
              max={new Date().toISOString().split('T')[0]}
            />
            <Button
              onClick={handleSaveDob}
              disabled={savingDob || !dob}
              className="bg-zinc-900 text-white"
              data-testid="save-dob-btn"
            >
              {savingDob ? 'Saving...' : dob && user?.date_of_birth === dob ? 'Saved' : 'Save'}
            </Button>
          </div>
          {dob && (() => {
            const age = Math.floor((new Date() - new Date(dob)) / (365.25 * 24 * 60 * 60 * 1000));
            return <p className="text-xs text-zinc-500 mt-2">Age: {age} years {age >= 18 ? '(18+ content accessible)' : '(Under 18 - restricted content blocked)'}</p>;
          })()}
        </div>

        {/* Location */}
        <div className="bg-white rounded-2xl border border-zinc-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-green-100 rounded-lg"><MapPin size={18} className="text-green-600" /></div>
            <div>
              <h3 className="font-display font-semibold text-lg">Location Services</h3>
              <p className="text-xs text-zinc-500">Enable to see nearby deals and services</p>
            </div>
          </div>
          <Button onClick={enableLocation} variant={locationEnabled ? 'ghost' : 'default'} className={locationEnabled ? 'text-green-600' : 'bg-zinc-900 text-white'} data-testid="enable-location-btn">
            {locationEnabled ? <><CheckCircle size={16} className="mr-2" /> Location Enabled</> : <><Globe size={16} className="mr-2" /> Enable Location</>}
          </Button>
        </div>

        <Button onClick={handleSave} disabled={saving} className="w-full bg-gradient-to-r from-[#b8ff00] to-[#00ff88] text-black font-bold py-6 rounded-xl" data-testid="save-settings-btn">
          <Save size={18} className="mr-2" /> {saving ? 'Saving...' : 'Save Preferences'}
        </Button>
      </div>
    </div>
  );
}
